import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daf8ij1tto0ceqqnh7u0";

const benefits = [
  { icon: "ri-chat-smile-3-line", title: "同好相聚", desc: "按城市分站匹配，找到你的同城同好与聊天搭子。" },
  { icon: "ri-notification-4-line", title: "一手资讯", desc: "超话、应援与见面会动态，第一时间触达不遗漏。" },
  { icon: "ri-gift-line", title: "专属福利", desc: "参与线上互动与应援，有机会获得限定电子特典与周边抽奖资格。" },
];

export default function JoinSection() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot: silently pass through with generic feedback, do not send
    const trap = String(fd.get("company_alt") || "").trim();
    if (trap) {
      setStatus("success");
      setMessage("已收到你的加入申请，我们会尽快通过对应城市分站与你联系。");
      return;
    }

    setSending(true);
    setStatus("idle");
    setMessage("");

    const payload = new URLSearchParams();
    fd.forEach((val, key) => {
      if (key === "company_alt") return;
      const v = String(val).trim();
      if (v) payload.append(key, v);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code || "";
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";

      const ok = res.ok && code === "OK";
      if (ok) {
        form.reset();
        setStatus("success");
        setMessage("申请已提交，欢迎加入吃瓜捷克粉丝社群，我们很快联系你。");
      } else {
        setStatus("error");
        setMessage(serverMsg ? `提交未成功：${serverMsg}` : "提交未成功，请稍后再试或直接邮件联系我们。");
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开小差了，请稍后再试。");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="join" className="bg-secondary-50">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          {/* left copy */}
          <div>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary-700">
              <i className="ri-team-line" />
              加入吃瓜捷克粉丝社群
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold tracking-tight text-foreground-950">
              填写申请，和同好们汇合
            </h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
              留下你的昵称、城市与常用邮箱，我们会按城市把资料推送给对应分站，由分站完成拉群与新人引导。
            </p>

            <ul className="mt-8 space-y-4">
              {benefits.map((b) => (
                <li key={b.title} className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background-50 text-xl text-secondary-700">
                    <i className={b.icon} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{b.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{b.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* form */}
          <div className="rounded-2xl border border-background-200/80 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">社群加入申请</h3>
            <p className="mt-1 text-sm text-foreground-500">填写以下信息，我们在 1—3 个工作日内联系你。</p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-readdy-form>
              <div>
                <label htmlFor="join-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  你的昵称
                </label>
                <input
                  id="join-name"
                  name="name"
                  type="text"
                  required
                  placeholder="例如：捷克的小番茄"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200"
                />
              </div>
              <div>
                <label htmlFor="join-city" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  所在城市
                </label>
                <input
                  id="join-city"
                  name="city"
                  type="text"
                  required
                  placeholder="例如：上海"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200"
                />
              </div>
              <div>
                <label htmlFor="join-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  常用邮箱
                </label>
                <input
                  id="join-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-secondary-500 focus:ring-2 focus:ring-secondary-200"
                />
              </div>

              {/* anti-spam honeypot */}
              <div className="fp-trap" aria-hidden="true">
                <label>
                  公司（可选）
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly />
                </label>
              </div>

              {status === "error" && message && (
                <p className="rounded-lg bg-primary-50 px-4 py-3 text-sm text-primary-800" role="alert">
                  <i className="ri-error-warning-line mr-1.5" />
                  {message}
                </p>
              )}
              {status === "success" && message && (
                <p className="rounded-lg bg-accent-50 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line mr-1.5" />
                  {message}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
              >
                <i className={sending ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                {sending ? "提交中…" : "提交申请"}
              </button>
              <p className="text-center text-xs text-foreground-500">
                提交即表示你愿意接收与捷克相关的活动通知，可随时退订。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}